"use client"

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react"

interface WaitlistContextValue {
  isOpen: boolean
  prefillCity: string
  geoCountry: string
  openWaitlist: (city?: string) => void
  openGlobalWaitlist: (geoCountry: string) => void
  closeWaitlist: () => void
}

const WaitlistContext = createContext<WaitlistContextValue>({
  isOpen: false,
  prefillCity: "",
  geoCountry: "",
  openWaitlist: () => {},
  openGlobalWaitlist: () => {},
  closeWaitlist: () => {},
})

export function WaitlistProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [prefillCity, setPrefillCity] = useState("")
  const [geoCountry, setGeoCountry] = useState("")
  const openWaitlist = useCallback((city?: string) => {
    if (city) setPrefillCity(city)
    setGeoCountry("")
    setIsOpen(true)
  }, [])
  const openGlobalWaitlist = useCallback((geo: string) => {
    setGeoCountry(geo)
    setIsOpen(true)
  }, [])
  const closeWaitlist = useCallback(() => {
    setIsOpen(false)
    setPrefillCity("")
    setGeoCountry("")
  }, [])

  // Memoize the context value so unrelated re-renders of the provider do not
  // force every `useWaitlist()` consumer (there is one in PageShell, i.e. on
  // every page) to re-render.
  const value = useMemo<WaitlistContextValue>(
    () => ({
      isOpen,
      prefillCity,
      geoCountry,
      openWaitlist,
      openGlobalWaitlist,
      closeWaitlist,
    }),
    [
      isOpen,
      prefillCity,
      geoCountry,
      openWaitlist,
      openGlobalWaitlist,
      closeWaitlist,
    ]
  )

  return (
    <WaitlistContext.Provider value={value}>
      {children}
    </WaitlistContext.Provider>
  )
}

export function useWaitlist(): WaitlistContextValue {
  return useContext(WaitlistContext)
}
